/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import {
  Alert,
  Box,
  DialogContentText,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReplayIcon from '@mui/icons-material/Replay';
import { DialogDefaultV2 } from '../../../components/v2';
import { renderStatus } from '../../../components/v2/cell-renderers/status';
import BaseService from '../../../services/BaseService';
import * as actionsToast from '../../../store/toast/actions';
import {
  canReprocess,
  formatJsonBody,
  statusToChip,
} from './constants';

const formatTs = (value) => {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isNaN(n) && n > 0) {
    return moment(n).format('DD/MM/YYYY HH:mm');
  }
  return moment(value).isValid() ? moment(value).format('DD/MM/YYYY HH:mm') : '—';
};

const DetalheIntegracao = ({
  open,
  row,
  onClose,
  onReload,
  history,
  messageSuccess,
  messageError,
}) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  const record = detail || row;

  const fetchDetail = useCallback(async () => {
    if (!row?.id) return;
    setLoading(true);
    try {
      const response = await BaseService.get({ url: `integrate-entities/${row.id}` });
      setDetail(response.data);
    } catch {
      setDetail(row);
    } finally {
      setLoading(false);
    }
  }, [row]);

  useEffect(() => {
    if (open && row?.id) {
      if (row.body != null || row.url != null) {
        setDetail(row);
      } else {
        fetchDetail();
      }
    } else {
      setDetail(null);
    }
  }, [open, row, fetchDetail]);

  const onReprocess = async () => {
    if (!record?.id) return;
    setReprocessing(true);
    try {
      const response = await BaseService.post({
        url: `integrate-entities/${record.id}/reprocessar`,
      });
      messageSuccess({
        message:
          response?.data?.message || 'Integração reprocessada com sucesso',
      });
      setOpenConfirm(false);
      onReload();
      onClose();
    } catch (error) {
      messageError({
        message:
          error?.response?.data?.message
          || error?.message
          || 'Falha ao reprocessar',
      });
    } finally {
      setReprocessing(false);
    }
  };

  const openNsir = () => {
    if (record?.nsirId) {
      history.push(`/nsir/${record.nsirId}`);
    }
  };

  const chip = record?.status ? statusToChip(record.status) : null;

  const headerActions = [
    {
      title: 'Abrir NSIR',
      onClick: openNsir,
      show: !!record?.nsirId,
    },
    {
      title: 'Reprocessar',
      onClick: () => setOpenConfirm(true),
      show: record && canReprocess(record.status),
      disabled: reprocessing,
    },
  ];

  return (
    <>
      <DialogDefaultV2
        open={open}
        handlerCancel={() => onClose()}
        handlerConfirm={() => onClose()}
        title="Detalhe da integração"
        confirmMessage="Fechar"
        cancelMessage={null}
        readOnly
        maxWidth="md"
        fullWidth
        headerAction={headerActions}
      >
        {loading && !record ? (
          <DialogContentText>Carregando…</DialogContentText>
        ) : !record ? (
          <DialogContentText>Nenhum registro selecionado.</DialogContentText>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
              {chip && renderStatus(chip)}
              {record.nsirCodigo && (
                <Typography variant="body2">
                  NSIR:{' '}
                  {record.nsirId ? (
                    <Link
                      component="button"
                      type="button"
                      onClick={openNsir}
                      sx={{ verticalAlign: 'baseline' }}
                    >
                      {record.nsirCodigo}
                      <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5 }} />
                    </Link>
                  ) : (
                    record.nsirCodigo
                  )}
                </Typography>
              )}
              {record.nsmpCodigo != null && (
                <Typography variant="body2">NSMP: {record.nsmpCodigo}</Typography>
              )}
            </Stack>

            {record.status === 'ERROR_SYNC' && record.error && (
              <Alert severity="error">{record.error}</Alert>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary">
                URL
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {record.url || '—'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Payload (body)
              </Typography>
              <Box
                component="pre"
                sx={{
                  mt: 0.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  overflow: 'auto',
                  maxHeight: 240,
                  fontSize: '0.75rem',
                  m: 0,
                }}
              >
                {formatJsonBody(record.body)}
              </Box>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Criado em
                </Typography>
                <Typography variant="body2">{formatTs(record.createdAt)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Atualizado em
                </Typography>
                <Typography variant="body2">{formatTs(record.updatedAt)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Sincronizado em
                </Typography>
                <Typography variant="body2">{formatTs(record.syncedAt)}</Typography>
              </Box>
            </Stack>

            {record.externalId && (
              <Typography variant="caption" color="text.secondary">
                ID externo (NSMP): {record.externalId}
              </Typography>
            )}
          </Stack>
        )}
      </DialogDefaultV2>

      <DialogDefaultV2
        open={openConfirm}
        handlerCancel={() => {
          if (reprocessing) return;
          setOpenConfirm(false);
        }}
        handlerConfirm={onReprocess}
        title="Reprocessar integração"
        confirmMessage="Sim"
        cancelMessage="Não"
        confirmLoading={reprocessing}
        disabledConfirmButton={reprocessing}
      >
        <DialogContentText>
          Deseja reenviar esta integração
          {record?.nsirCodigo ? ` (NSIR ${record.nsirCodigo})` : ''}?
          {reprocessing ? ' Enviando ao sistema terceiro…' : ''}
        </DialogContentText>
      </DialogDefaultV2>
    </>
  );
};

DetalheIntegracao.propTypes = {
  open: PropTypes.bool.isRequired,
  row: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onReload: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  messageSuccess: PropTypes.func.isRequired,
  messageError: PropTypes.func.isRequired,
};

DetalheIntegracao.defaultProps = {
  row: null,
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...actionsToast }, dispatch);

export default connect(null, mapDispatchToProps)(withRouter(DetalheIntegracao));
